import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Button,
  Avatar,
  TextField,
  Typography,
  Container,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Badge,
  Fade,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  Dumbbell,
  Play,
  FileText,
  Send,
  Reply,
  Upload,
  Video,
  X,
  Repeat,
  Gauge,
  Import,
  Trash2,
} from 'lucide-react';
import { Icon } from '@iconify/react';
import { useAuthContext } from 'src/auth/hooks';
import { Lightbox, useLightBox } from 'src/components/lightbox';
import {
  AddComment_set,
  AddCommentMedia_set,
  get_comments_by_id_set,
} from 'src/auth/context/jwt/program';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router';
import { LoadingButton } from '@mui/lab';
import { ChatThreadSkeleton } from './chat-skeleton';
import {
  AddMemberComment_set,
  AddMemberCommentMedia_set,
  delete_note_by_type,
  get_set_comments,
} from 'src/auth/context/jwt/library';
import { paths } from 'src/routes/paths';

// Workout Details Card
function WorkoutDetailsCard({ set }) {
  const [showTutorials, setShowTutorials] = useState(true);

  // Separate videos and images from comments
  const mediaFromComments = set?.comments
    .filter((comment) => comment.media && comment.media.length > 0)
    .flatMap((comment) =>
      comment.media.map((media) => ({
        ...media,
        commentAuthor: comment.user?.name,
        commentDate: comment.created_at,
        commentMessage: comment.message,
      }))
    );

  const videos = mediaFromComments.filter((item) => item.type === 'video');
  const images = mediaFromComments.filter((item) => item.type === 'image');

  // Prepare slides for video lightbox
  const videoSlides = videos.map((video) => ({
    type: 'video',
    width: 1280,
    height: 720,
    sources: [
      {
        src: video.url,
        type: 'video/mp4',
      },
    ],
    title: `Video by ${video.commentAuthor}`,
    description: video.commentMessage,
  }));

  // Prepare slides for image lightbox
  const imageSlides = images.map((image) => ({
    type: 'image',
    sources: [
      {
        src: image.url,
      },
    ],
    src: image.url,
    title: `Image by ${image.commentAuthor}`,
    description: image.commentMessage,
  }));

  const videoLightbox = useLightBox(videoSlides);
  const imageLightbox = useLightBox(imageSlides);

  return (
    <>
      <Card
        elevation={0}
        sx={{
          mb: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            p: 4,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Dumbbell size={32} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="overline" sx={{ opacity: 0.9 }}>
                {set?.exercise_name}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                {set?.set?.setTag}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {(set?.set?.effort !== null || set?.set?.effort_value !== 0) && (
              <Chip
                color="info"
                icon={<Gauge size={16} color="white" />}
                label={`Effort ${set?.set?.effort || ''}  ${set?.set?.effort_value ? `(${set?.set?.effort_value})` : ''} `}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />
            )}

            {set?.set?.reps !== null && (
              <Chip
                color="info"
                icon={<Repeat size={16} color="white" />}
                label={`Reps ${set?.set?.reps}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />
            )}

            {(set?.set?.weight !== null || set?.set?.unit !== null) && (
              <Chip
                color="info"
                icon={<Dumbbell size={16} color="white" />}
                label={`Weight ${set?.set?.weight || ''} ${set?.set?.unit || ''}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
              />
            )}
          </Stack>
        </Box>

        {/* Tutorial Section */}
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            {videos?.length === 0 && images.length === 0 ? (
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                No Videos / Images
              </Typography>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Videos & Images
                </Typography>
                <Button
                  size="small"
                  onClick={() => setShowTutorials(!showTutorials)}
                  sx={{ color: '#667eea' }}
                >
                  {showTutorials ? 'Hide' : 'Show'}
                </Button>
              </>
            )}
          </Stack>

          {showTutorials && (
            <>
              {/* Videos Section */}
              {videos.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Videos ({videos.length})
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      overflowX: 'auto',
                      pb: 2,
                      '&::-webkit-scrollbar': {
                        height: 8,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#667eea',
                        borderRadius: 4,
                      },
                    }}
                  >
                    {videos.map((video, index) => (
                      <Card
                        key={index}
                        onClick={() => videoLightbox.onOpen(video.url)}
                        sx={{
                          minWidth: 200,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: 200,
                            height: 150,
                            bgcolor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <IconButton
                            sx={{
                              bgcolor: 'rgba(102, 126, 234, 0.9)',
                              color: 'white',
                              '&:hover': { bgcolor: 'rgba(102, 126, 234, 1)' },
                            }}
                          >
                            <Play size={32} />
                          </IconButton>
                        </Box>
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            By {video.commentAuthor}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </>
              )}

              {/* Images Section */}
              {images.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Images ({images.length})
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      overflowX: 'auto',
                      pb: 2,
                      '&::-webkit-scrollbar': {
                        height: 8,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#667eea',
                        borderRadius: 4,
                      },
                    }}
                  >
                    {images.map((image, index) => (
                      <Card
                        key={index}
                        onClick={() => imageLightbox.onOpen(image.url)}
                        sx={{
                          minWidth: 200,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="150"
                          image={image.url}
                          alt={`Image by ${image.commentAuthor}`}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            By {image.commentAuthor}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Video Lightbox */}
      <Lightbox
        index={videoLightbox.selected}
        slides={videoSlides}
        open={videoLightbox.open}
        close={videoLightbox.onClose}
        disableSlideshow
        disableZoom
      />

      {/* Image Lightbox */}
      <Lightbox
        index={imageLightbox.selected}
        slides={imageSlides}
        open={imageLightbox.open}
        close={imageLightbox.onClose}
        disableSlideshow
      />
    </>
  );
}

// Media Preview Component
function MediaPreview({ file, onRemove }) {
  const isVideo = file?.type?.includes('video');

  return (
    <Box
      sx={{
        position: 'relative',
        width: 120,
        height: 120,
        borderRadius: 2,
        overflow: 'hidden',
        border: '2px solid #667eea',
      }}
    >
      {isVideo ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Video size={40} color="white" />
        </Box>
      ) : (
        <Box
          component="img"
          src={file.preview || file.media}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'white',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
        }}
      >
        <X size={16} />
      </IconButton>
    </Box>
  );
}

// Comment Form Component
function CommentForm({ onSubmit, loading, disable, library_program }) {
  const { user } = useAuthContext();
  const [comment, setComment] = useState('');
  const [media, setMedia] = useState([]); // array of files with upload status

  // Upload single file (replace loading state with preview after upload)
  const Upload_Post_Media = async (file, index) => {
    try {
      const res = library_program
        ? await AddMemberCommentMedia_set({
            file,
            type: file?.type?.includes('video') ? 'video' : 'image',
          })
        : await AddCommentMedia_set({
            file,
            type: file?.type?.includes('video') ? 'video' : 'image',
          });

      // Replace uploading placeholder with uploaded data
      setMedia((prev) =>
        prev.map((m, i) =>
          i === index
            ? {
                ...m,
                uploading: false,
                id: res?.data?.id,
                media: res?.data?.media, // URL for preview
              }
            : m
        )
      );
    } catch (error) {
      toast.error(typeof error === 'string' ? error : error?.error);
      setMedia((prev) => prev.filter((_, i) => i !== index)); // remove failed upload
    }
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);

    // Step 1: Immediately show loading placeholders
    const placeholders = files.map((file) => ({
      file,
      type: file.type,
      uploading: true,
    }));
    setMedia((prev) => [...prev, ...placeholders]);

    // Step 2: Upload each file sequentially
    for (let i = 0; i < files.length; i += 1) {
      const globalIndex = media.length + i; // correct index considering existing items
      // eslint-disable-next-line no-await-in-loop
      await Upload_Post_Media(files[i], globalIndex);
    }
    e.target.value = null;
  };

  // Handle submit — send only comma-separated IDs
  const handleSubmit = (e) => {
    e.preventDefault();
    if (comment.trim() || media.length > 0) {
      const uploadedIds = media.map((m) => m.id).filter(Boolean);
      const mediaIdsString = uploadedIds.join(',');
      console.log(mediaIdsString, comment, '✅ Final Submission Data');
      onSubmit({ comment, comment_media_ids: mediaIdsString });
      setComment('');
      setMedia([]);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 4,
        border: '2px solid #667eea20',
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: '#667eea',
                color: 'white',
              }}
            >
              <FileText size={20} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {user?.role?.id === 4
                ? 'Add a comment'
                : 'Add notes / track progress / remember tips for your next workout.'}
            </Typography>
          </Stack>

          {/* Comment field */}
          <TextField
            fullWidth
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Note anything you want to remember. Write, add photos, or upload videos…"
            variant="outlined"
          />

          {/* Media Previews */}
          {media.length > 0 && (
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
              {media.map((item, idx) => (
                <Box key={idx} sx={{ position: 'relative' }}>
                  {item.uploading ? (
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: 2,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: '2px solid #667eea',
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: '#667eea' }} />
                    </Box>
                  ) : (
                    <MediaPreview
                      file={item}
                      onRemove={() => setMedia(media.filter((_, i) => i !== idx))}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          )}

          {/* Actions */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              disabled={disable}
              component="label"
              startIcon={<Upload size={18} />}
              sx={{ color: '#667eea' }}
            >
              Upload Media (Multiple)
              <input
                type="file"
                hidden
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
              />
            </Button>

            <LoadingButton
              loading={loading}
              type="submit"
              variant="contained"
              disabled={
                (!comment.trim() && media.length === 0) || media.some((m) => m.uploading) || disable
              }
              endIcon={<Send size={18} />}
              sx={{
                bgcolor: '#667eea',
                '&:hover': { bgcolor: '#5568d3' },
                borderRadius: 2,
                px: 3,
              }}
            >
              Post Comment
            </LoadingButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Reply Form Component
function ReplyForm({ loading, parent, onSubmit, onCancel, replyTo, disable, library_program }) {
  const [reply, setReply] = useState('');
  const [media, setMedia] = useState([]);

  // Upload single file (replace loading state with preview after upload)
  const Upload_Post_Media = async (file, index) => {
    try {
      const res = library_program
        ? await AddMemberCommentMedia_set({
            file,
            type: file?.type?.includes('video') ? 'video' : 'image',
          })
        : await AddCommentMedia_set({
            file,
            type: file?.type?.includes('video') ? 'video' : 'image',
          });

      // Replace uploading placeholder with uploaded data
      setMedia((prev) =>
        prev.map((m, i) =>
          i === index
            ? {
                ...m,
                uploading: false,
                id: res?.data?.id,
                media: res?.data?.media, // URL for preview
              }
            : m
        )
      );
    } catch (error) {
      toast.error(typeof error === 'string' ? error : error?.error);
      setMedia((prev) => prev.filter((_, i) => i !== index)); // remove failed upload
    }
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);

    // Step 1: Immediately show loading placeholders
    const placeholders = files.map((file) => ({
      file,
      type: file.type,
      uploading: true,
    }));
    setMedia((prev) => [...prev, ...placeholders]);

    // Step 2: Upload each file sequentially
    for (let i = 0; i < files.length; i += 1) {
      const globalIndex = media.length + i; // correct index considering existing items
      // eslint-disable-next-line no-await-in-loop
      await Upload_Post_Media(files[i], globalIndex);
    }

    e.target.value = null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reply.trim() || media.length > 0) {
      const uploadedIds = media.map((m) => m.id).filter(Boolean);
      const mediaIdsString = uploadedIds.join(',');
      onSubmit({ parent_id: parent?.id, comment: reply, comment_media_ids: mediaIdsString });
      setReply('');
      setMedia([]);
    }
  };
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: 'background.neutral',
        borderRadius: 2,
        border: '1px solid #667eea20',
      }}
    >
      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
        <Typography variant="caption" color="text.secondary">
          Replying to <strong>@{replyTo}</strong>
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={2}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Write your reply..."
          variant="outlined"
          size="small"
        />

        {media.map((item, idx) => (
          <Box key={idx} sx={{ position: 'relative' }}>
            {item.uploading ? (
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  bgcolor: 'rgba(0,0,0,0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '2px solid #667eea',
                }}
              >
                <CircularProgress size={32} sx={{ color: '#667eea' }} />
              </Box>
            ) : (
              <MediaPreview
                file={item}
                onRemove={() => setMedia(media.filter((_, i) => i !== idx))}
              />
            )}
          </Box>
        ))}

        <Stack direction="row" justifyContent="space-between">
          <Button
            component="label"
            size="small"
            startIcon={<Upload size={16} />}
            sx={{ color: '#667eea' }}
            disabled={disable}
          >
            Add Media (Multiple)
            <input
              type="file"
              hidden
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
            />
          </Button>

          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={onCancel} variant="outlined">
              Cancel
            </Button>
            <LoadingButton
              loading={loading}
              type="submit"
              size="small"
              variant="contained"
              disabled={
                (!reply.trim() && media.length === 0) || media.some((m) => m.uploading) || disable
              }
              sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5568d3' } }}
            >
              Reply
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

// Comment Media Display
function CommentMedia({ media, commentAuthor, commentMessage }) {
  if (!media || media.length === 0) return null;

  // Prepare slides for lightbox (supports both images and videos)
  const mediaSlides = media.map((item) => {
    if (item.type === 'video') {
      return {
        type: 'video',
        width: 1280,
        height: 720,
        sources: [
          {
            src: item.url,
            type: 'video/mp4',
          },
        ],
        title: `Video by ${commentAuthor}`,
        description: commentMessage,
      };
    } else {
      return {
        type: 'image',
        sources: [
          {
            src: item.url,
          },
        ],
        src: item.url,
        title: `Image by ${commentAuthor}`,
        description: commentMessage,
      };
    }
  });

  const mediaLightbox = useLightBox(mediaSlides);

  return (
    <>
      <Grid container spacing={1} sx={{ mt: 1 }}>
        {media.map((item, idx) => (
          <Grid item xs={6} sm={4} md={3} key={idx}>
            <Card
              elevation={0}
              onClick={() => mediaLightbox.onOpen(item.url)}
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  opacity: 0.9,
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              {item.type === 'video' ? (
                <Box sx={{ position: 'relative', paddingTop: '100%', bgcolor: '#000' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconButton
                      sx={{
                        bgcolor: 'rgba(102, 126, 234, 0.9)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(102, 126, 234, 1)' },
                      }}
                    >
                      <Play size={32} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <CardMedia
                  component="img"
                  image={item.url}
                  alt={`Upload ${idx + 1}`}
                  sx={{ aspectRatio: '1/1', objectFit: 'cover' }}
                />
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Media Lightbox for comments */}
      <Lightbox
        index={mediaLightbox.selected}
        slides={mediaSlides}
        open={mediaLightbox.open}
        close={mediaLightbox.onClose}
        disableSlideshow
      />
    </>
  );
}

// Comment Item Component
function CommentItem({
  comment,
  onReply,
  index,
  reply_commenting,
  setreply_commenting,
  disable,
  library_program,
  onRefresh,
  entity_type,
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingReplyId, setDeletingReplyId] = useState(null);

  const handle_delete_comment = async (commentId, isReply = false, replyId = null) => {
    try {
      if (isReply) {
        setDeletingReplyId(replyId);
      } else {
        setDeleting(true);
      }

      const res = await delete_note_by_type({
        id: isReply ? replyId : commentId,
        entity_type,
      });

      toast.success(res?.message);

      // Call the onDelete callback if provided, otherwise use onRefresh
      onRefresh && onRefresh();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : error?.error);
    } finally {
      if (isReply) {
        setDeletingReplyId(null);
      } else {
        setDeleting(false);
      }
    }
  };

  return (
    <Fade in timeout={300 + index * 100}>
      <Card
        elevation={0}
        sx={{
          mb: 2,
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      border: '2px solid white',
                    }}
                  />
                }
              >
                <Avatar
                  alt={comment?.user?.name}
                  src={comment?.user?.avatar}
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    bgcolor: '#667eea',
                    fontWeight: 600,
                  }}
                >
                  {comment?.user?.name?.charAt(0).toUpperCase()}
                </Avatar>
              </Badge>

              <Box sx={{ flexGrow: 1 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                  spacing={{ xs: 1, sm: 0 }}
                >
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      >
                        {comment?.user?.name}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {new Date(comment?.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => setShowReplyForm(!showReplyForm)}
                      sx={{ color: showReplyForm ? '#667eea' : 'text.secondary' }}
                    >
                      <Reply size={18} />
                    </IconButton>

                    {/* Delete button for main comment */}
                    <IconButton
                      size="small"
                      onClick={() => handle_delete_comment(comment?.id, false)}
                      disabled={deleting}
                      sx={{ color: 'error.main' }}
                    >
                      {deleting ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </IconButton>
                  </Stack>
                </Stack>

                <Typography
                  variant="body1"
                  sx={{ mt: 1.5, lineHeight: 1.7, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {comment?.message}
                </Typography>

                <CommentMedia
                  media={comment.media}
                  commentAuthor={comment.author}
                  commentMessage={comment.message}
                />

                {/* Replies */}
                {comment.replies && comment?.replies?.length > 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      ml: { xs: 1, sm: 2 },
                      pl: { xs: 2, sm: 3 },
                      borderLeft: '2px solid #e2e8f0',
                    }}
                  >
                    {comment.replies.map((reply) => (
                      <Box key={reply?.id} sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={2}>
                          <Avatar
                            src={reply?.user?.avatar}
                            sx={{ width: 32, height: 32, bgcolor: '#667eea' }}
                          >
                            {reply.user?.name?.charAt(0)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              spacing={1}
                              justifyContent="space-between"
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                  }}
                                >
                                  {reply?.user?.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                                >
                                  {new Date(reply?.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </Typography>
                              </Stack>

                              {/* Delete button for reply */}
                              <IconButton
                                size="small"
                                onClick={() => handle_delete_comment(comment?.id, true, reply?.id)}
                                disabled={deletingReplyId === reply?.id}
                                sx={{ color: 'error.main' }}
                              >
                                {deletingReplyId === reply?.id ? (
                                  <CircularProgress size={14} color="error" />
                                ) : (
                                  <Trash2 size={16} />
                                )}
                              </IconButton>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                              {reply?.message}
                            </Typography>
                            <CommentMedia
                              media={reply?.media}
                              commentAuthor={reply?.user?.name}
                              commentMessage={reply.message}
                            />
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Reply Form */}
                {showReplyForm && (
                  <ReplyForm
                    disable={disable}
                    loading={reply_commenting}
                    parent={comment}
                    replyTo={comment.user?.name}
                    onSubmit={(data) => {
                      setreply_commenting(true);
                      onReply(data);
                    }}
                    onCancel={() => setShowReplyForm(false)}
                    library_program={library_program}
                  />
                )}
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );
}

// Main Component
export default function SetDiscussion() {
  const { user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  const { set, exercise, program, activity_id, release_program, library_program } =
    location.state || {};

  const [comments, setComments] = useState([]);
  const [entity_type, setentity_type] = useState([]);

  const workoutData = {
    program,
    exercise_name: exercise?.name,
    set,
    comments,
  };

  const [get_loading, setget_loading] = useState(false);
  const [status, setstatus] = useState(null);

  const get_comments = async () => {
    try {
      const res =
        release_program || library_program
          ? await get_set_comments({ set_id: set?.id, activity_id })
          : await get_comments_by_id_set({ set_id: set?.original_id, activity_id });

      setComments(res?.data?.data);
      setentity_type(res?.data?.entity_type);
      setstatus(res?.data?.status);
    } catch (error) {
      console.log(error);
    } finally {
      setget_loading(false);
    }
  };

  useEffect(() => {
    setget_loading(true);
    get_comments();
    // eslint-disable-next-line
  }, [exercise?.id]);

  const [commenting, setcommenting] = useState(false);
  const [reply_commenting, setreply_commenting] = useState(false);

  const handleAddComment = async (data) => {
    setcommenting(true);
    try {
      library_program
        ? await AddMemberComment_set({ ...data, set_id: set?.original_id }).then(async (res) => {
            await get_comments();
          })
        : await AddComment_set({ ...data, set_id: set?.original_id }).then(async (res) => {
            await get_comments();
          });
    } catch (error) {
      console.log(error);
    } finally {
      setcommenting(false);
      setreply_commenting(false);
      // setpostLoading(false);
    }
  };

  const returnNote = (st) => {
    switch (st?.toLowerCase()) {
      case 'draft':
        return 'You cannot comment until the set is sent for approval.';
      case 'approval_requested':
        return 'Comments are disabled until the member approves this set.';
      case 'changes_requested':
        return 'Comments are disabled until the trainer updates the set and it is approved again.';
      case 'approved':
        return 'Comments are disabled until the trainer make approved changes active'; // comments allowed after approval
      case 'active':
        return '';
      case 'completed':
        return 'This set is completed. You can view old comments but cannot add new ones.';
      default:
        return '';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {status && status !== 'active' && status !== 'deleted' && status !== 'realeased' && (
        <Box
          sx={{
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'grey.200',
            borderRadius: 2,
            p: 2,
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            mb: 2,
          }}
        >
          <Box style={{ display: 'flex' }}>
            <Typography
              variant="subtitle2"
              sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}
            >
              Note:
            </Typography>

            <Icon icon="mdi:information" style={{ fontSize: '18px', marginLeft: 5 }} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              color: 'text.primary',
              lineHeight: 1.5,
              textTransform: 'capitalize',
            }}
          >
            {returnNote(status) || ''}
          </Typography>
        </Box>
      )}

      {!get_loading && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Import size={18} />}
            onClick={() => {
              navigate(paths.dashboard.general.NotesHistory, {
                state: {
                  currentSet: set?.setTag,
                  currentWorkout: exercise?.name,
                  currentProgram: program,
                  entity_id: set?.original_id,
                  entity_type,
                },
              });
            }}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#5568d3',
                backgroundColor: 'rgba(102, 126, 234, 0.04)',
              },
              borderRadius: 2,
              px: 3,
            }}
          >
            Notes History
          </Button>
        </Stack>
      )}

      <CommentForm
        disable={status !== null && status !== 'active'}
        onSubmit={handleAddComment}
        loading={commenting}
        library_program={library_program}
      />

      {get_loading ? (
        <ChatThreadSkeleton />
      ) : (
        <>
          <WorkoutDetailsCard set={workoutData} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Discussion ({comments.length} comments)
            </Typography>
            <Divider />
          </Box>

          <Stack spacing={0}>
            {comments.map((comment, index) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onReply={handleAddComment}
                index={index}
                reply_commenting={reply_commenting}
                setreply_commenting={setreply_commenting}
                disable={status !== null && status !== 'active'}
                library_program={library_program}
                entity_type={entity_type}
                onRefresh={get_comments}
              />
            ))}
          </Stack>
        </>
      )}
    </Container>
  );
}
